import {
  Edit,
  Eye,
  KeyRound,
  Loader2,
  Search,
  Shield,
  User,
  UserCircle
} from 'lucide-react';
import { useContext, useEffect, useRef, useState } from 'react';

import Alert from '@/components/Alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

import { ChainlitContext } from 'client-types/*';

import UserDetailModal from './UserDetailModal';

interface AdminUser {
  id: string;
  identifier: string;
  first_name?: string;
  last_name?: string;
  role: string;
  provider: string;
  created_at: string;
  subscription_status?: string;
  plan_tier?: string; // 'month' | 'year'
  mailchimp_synced?: boolean;
  mailchimp_status?: string;
}

interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
}

// Deterministic avatar tint from the email so a user keeps the same color.
const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200',
  'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200',
  'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200',
  'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-200'
];

const colorFor = (s: string) =>
  AVATAR_COLORS[
    [...s].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length
  ];

// Full name from first/last, falling back to the email's local part.
const displayName = (user: AdminUser) => {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  if (name) return name;
  return (user.identifier.split('@')[0] || user.identifier).trim();
};

const getInitials = (user: AdminUser) => {
  if (user.first_name || user.last_name) {
    const f = (user.first_name || '').trim();
    const l = (user.last_name || '').trim();
    const letters = (f[0] || '') + (l[0] || f[1] || '');
    if (letters) return letters.toUpperCase();
  }
  const local = (user.identifier.split('@')[0] || user.identifier).trim();
  const parts = local.split(/[._\-+]+/).filter(Boolean);
  const letters =
    parts.length >= 2 ? parts[0][0] + parts[1][0] : local.slice(0, 2);
  return letters.toUpperCase();
};

// Auth provider → friendly label + icon (Password vs Google/OAuth).
const providerMeta = (provider: string) => {
  const p = (provider || '').toLowerCase();
  if (p.includes('google')) return { label: 'Google', Icon: UserCircle };
  if (p.includes('credential') || p === 'password' || p.includes('email'))
    return { label: 'Password', Icon: KeyRound };
  const label = provider
    ? provider.charAt(0).toUpperCase() + provider.slice(1)
    : 'Unknown';
  return { label, Icon: UserCircle };
};

const subStatusClass = (status?: string) => {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'canceled':
    case 'past_due':
      return 'text-destructive';
    default:
      return 'text-muted-foreground';
  }
};

const planLabel = (tier?: string) =>
  tier === 'month' ? 'Monthly Plan' : tier === 'year' ? 'Yearly Plan' : null;

const UserManagementTable = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showActive, setShowActive] = useState(true);
  const [showCanceled, setShowCanceled] = useState(false);
  const [total, setTotal] = useState(0);
  const pageRef = useRef(1);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [newRole, setNewRole] = useState<string>('');
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedUserForDetails, setSelectedUserForDetails] =
    useState<AdminUser | null>(null);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);

  const apiClient = useContext(ChainlitContext);
  const limit = 20;

  // 'active' | 'canceled' | 'all' — both checkboxes on means 'all'.
  const statusFilter = showActive && showCanceled ? 'all' : showCanceled ? 'canceled' : 'active';

  // Keep at least one of Active/Canceled selected at all times.
  const handleActiveChange = (checked: boolean) => {
    if (!checked && !showCanceled) return;
    setShowActive(checked);
  };

  const handleCanceledChange = (checked: boolean) => {
    if (!checked && !showActive) return;
    setShowCanceled(checked);
  };

  // "All" selects both; unchecking it falls back to Active-only.
  const handleAllChange = (checked: boolean) => {
    setShowActive(true);
    setShowCanceled(checked);
  };

  // append=false replaces the list (new filter/search); append=true adds the next batch (infinite scroll).
  const fetchPage = async (pageNum: number, append: boolean) => {
    try {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: String(pageNum),
        limit: String(limit),
        status_filter: statusFilter,
        role_filter: filterRole,
        search: debouncedSearchTerm
      });

      const response = await fetch(
        apiClient.buildEndpoint(`/api/admin/users?${params.toString()}`),
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch users');
      }

      const data: AdminUsersResponse = await response.json();
      setUsers((prev) => (append ? [...prev, ...data.users] : data.users));
      setTotal(data.total);
      pageRef.current = pageNum;
    } catch (err: any) {
      setError(err.message);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  };

  const hasMore = users.length < total;

  const loadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    fetchPage(pageRef.current + 1, true);
  };

  const handleRoleUpdate = async () => {
    if (!selectedUser || !newRole) return;

    try {
      setUpdating(true);
      const response = await fetch(
        apiClient.buildEndpoint(`/api/admin/users/${selectedUser.id}/role`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ role: newRole })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update role');
      }

      // Refresh users list
      await fetchPage(1, false);
      setShowRoleDialog(false);
      setSelectedUser(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const openRoleDialog = (user: AdminUser) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowRoleDialog(true);
  };

  const openUserDetailModal = (user: AdminUser) => {
    setSelectedUserForDetails(user);
    setShowUserDetailModal(true);
  };

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Any filter/search/status change does one clean reload from page 1.
  useEffect(() => {
    fetchPage(1, false);
  }, [statusFilter, filterRole, debouncedSearchTerm]);

  // Infinite scroll: load the next batch when the sentinel comes into view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, statusFilter, filterRole, debouncedSearchTerm]);

  const getRoleBadgeColor = (role: string) => {
    return role === 'ADMIN' ? 'default' : 'secondary';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Small reusable cells so desktop table and mobile cards stay in sync.
  const ProviderCell = ({ provider }: { provider: string }) => {
    const { label, Icon } = providerMeta(provider);
    return (
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="text-sm">{label}</span>
      </div>
    );
  };

  const SubscriptionCell = ({ user }: { user: AdminUser }) => {
    if (!user.subscription_status) {
      return (
        <span className="italic text-muted-foreground">No subscription</span>
      );
    }
    const label =
      user.subscription_status.charAt(0).toUpperCase() +
      user.subscription_status.slice(1);
    return (
      <div className="flex flex-col">
        <span className={`font-semibold ${subStatusClass(user.subscription_status)}`}>
          {label}
        </span>
        {planLabel(user.plan_tier) && (
          <span className="text-[10px] text-muted-foreground">
            {planLabel(user.plan_tier)}
          </span>
        )}
      </div>
    );
  };

  const MailchimpCell = ({ user }: { user: AdminUser }) => {
    const synced = !!user.mailchimp_synced;
    return (
      <div className="flex items-center gap-2" title={user.mailchimp_status || ''}>
        <span
          className={`h-2 w-2 rounded-full ${
            synced ? 'bg-emerald-500' : 'bg-muted-foreground/40'
          }`}
        />
        <span
          className={`text-sm ${
            synced
              ? 'font-medium text-emerald-600 dark:text-emerald-400'
              : 'text-muted-foreground'
          }`}
        >
          {synced ? 'Synced' : 'Not Synced'}
        </span>
      </div>
    );
  };

  const RoleBadge = ({ role }: { role: string }) => (
    <Badge
      variant={getRoleBadgeColor(role)}
      className="flex w-fit items-center gap-1"
    >
      {role === 'ADMIN' ? (
        <Shield className="h-3 w-3" />
      ) : (
        <User className="h-3 w-3" />
      )}
      {role}
    </Badge>
  );

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Search and Filter Controls */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
          <Input
            placeholder="Filter by name, email or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="USER">User</SelectItem>
            <SelectItem value="ADMIN">Admin</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="sub-filter-active"
              checked={showActive}
              onCheckedChange={(v) => handleActiveChange(v as boolean)}
            />
            <Label htmlFor="sub-filter-active" className="text-sm font-medium">
              Active
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="sub-filter-canceled"
              checked={showCanceled}
              onCheckedChange={(v) => handleCanceledChange(v as boolean)}
            />
            <Label htmlFor="sub-filter-canceled" className="text-sm font-medium">
              Canceled
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="sub-filter-all"
              checked={showActive && showCanceled}
              onCheckedChange={(v) => handleAllChange(v as boolean)}
            />
            <Label htmlFor="sub-filter-all" className="text-sm font-medium">
              All
            </Label>
          </div>
        </div>
      </div>

      {/* Users Table - Desktop */}
      <div className="hidden rounded-lg border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Subscription</TableHead>
              {/* Temporarily hidden Mailchimp column */}
              {/* <TableHead>Mailchimp</TableHead> */}
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10">
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="text-sm">Loading users...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-6 text-center text-muted-foreground"
                >
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback
                          className={`text-xs font-semibold ${colorFor(
                            user.identifier
                          )}`}
                        >
                          {getInitials(user)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="truncate font-medium">
                          {displayName(user)}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {user.identifier}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <RoleBadge role={user.role} />
                  </TableCell>
                  <TableCell>
                    <ProviderCell provider={user.provider} />
                  </TableCell>
                  <TableCell>
                    <SubscriptionCell user={user} />
                  </TableCell>
                  {/* Temporarily hidden Mailchimp column */}
                  {/* <TableCell>
                    <MailchimpCell user={user} />
                  </TableCell> */}
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(user.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openUserDetailModal(user)}
                        className="h-8 w-8"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openRoleDialog(user)}
                        className="h-8 w-8"
                        title="Edit Role"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Users Cards - Mobile */}
      <div className="space-y-3 md:hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading users...</span>
          </div>
        ) : users.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground">
            No users found
          </div>
        ) : (
          users.map((user) => (
            <div key={user.id} className="space-y-3 rounded-lg border p-4">
              <div className="flex items-start justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback
                      className={`text-xs font-semibold ${colorFor(
                        user.identifier
                      )}`}
                    >
                      {getInitials(user)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      {displayName(user)}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {user.identifier}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openUserDetailModal(user)}
                    className="h-8 w-8"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openRoleDialog(user)}
                    className="h-8 w-8"
                    title="Edit Role"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="mb-1 text-muted-foreground">Role</div>
                  <RoleBadge role={user.role} />
                </div>
                <div>
                  <div className="mb-1 text-muted-foreground">Provider</div>
                  <ProviderCell provider={user.provider} />
                </div>
                <div>
                  <div className="mb-1 text-muted-foreground">Subscription</div>
                  <SubscriptionCell user={user} />
                </div>
                {/* Temporarily hidden Mailchimp field */}
                {/* <div>
                  <div className="mb-1 text-muted-foreground">Mailchimp</div>
                  <MailchimpCell user={user} />
                </div> */}
                <div>
                  <div className="mb-1 text-muted-foreground">Joined</div>
                  <div className="text-sm">{formatDate(user.created_at)}</div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Infinite scroll: status text + sentinel the IntersectionObserver watches */}
      <div className="flex flex-col items-center gap-2 py-2">
        {!loading && (
          <p className="text-sm text-muted-foreground">
            Showing {users.length} of {total} users
          </p>
        )}
        {loadingMore && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading more...
          </div>
        )}
        {!loading && !hasMore && users.length > 0 && (
          <p className="text-xs text-muted-foreground">End of list</p>
        )}
        <div ref={sentinelRef} className="h-px w-full" />
      </div>

      {/* Role Update Dialog */}
      <AlertDialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update User Role</AlertDialogTitle>
            <AlertDialogDescription>
              Update the role for user: {selectedUser?.identifier}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger>
                <SelectValue placeholder="Select new role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USER">USER</SelectItem>
                <SelectItem value="ADMIN">ADMIN</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRoleUpdate}
              disabled={updating || !newRole || newRole === selectedUser?.role}
            >
              {updating ? 'Updating...' : 'Update Role'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Detail Modal */}
      <UserDetailModal
        user={selectedUserForDetails}
        isOpen={showUserDetailModal}
        onClose={() => setShowUserDetailModal(false)}
      />
    </div>
  );
};

export default UserManagementTable;